import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye } from 'lucide-react';
import LoopComments from '@/components/loops/LoopComments';
import { LinkifyContent } from '@/components/shared/LinkifyContent';
import BulletinAuthor from '@/components/bulletins/BulletinAuthor';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

interface LightboxInfoPanelProps {
  show: boolean;
  bulletin: any;
}

const LightboxInfoPanel: React.FC<LightboxInfoPanelProps> = ({ show, bulletin }) => {
  const userId = bulletin?.user_id;
  const { data: profile } = useQuery({
    queryKey: ['profile', userId],
    enabled: !!userId,
    initialData: bulletin?.profiles || null,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, profile_image_url, is_admin, is_premium, is_partner')
        .eq('id', userId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ x: 400, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 400, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed right-0 top-0 h-full w-[400px] bg-[#1A1A1B] border-l border-[#343536]/50 shadow-2xl overflow-y-auto z-60"
        >
          <div className="p-6 space-y-6">
            {profile?.username && (
              <div className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Artist</div>
                <BulletinAuthor
                  username={profile.username}
                  profileImageUrl={profile.profile_image_url}
                  isAdmin={profile.is_admin}
                  isPremium={profile.is_premium}
                  isPartner={profile.is_partner}
                />
              </div>
            )}

            {/* Title */}
            {bulletin.content && (
              <div>
                <h2 className="text-2xl font-semibold tracking-tight leading-tight">
                  {bulletin.content.split('\n')[0] || 'No caption'}
                </h2>
              </div>
            )}

            {/* Description - show only if there's content after first line */}
            {bulletin.content && bulletin.content.split('\n').slice(1).join('\n').trim() && (
              <div className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Description</div>
                <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/80">
                  <LinkifyContent content={bulletin.content.split('\n').slice(1).join('\n')} authorId={bulletin.user_id} />
                </p>
              </div>
            )}

            {/* Divider - only show if description exists */}
            {bulletin.content && bulletin.content.split('\n').slice(1).join('\n').trim() && (
              <div className="border-t border-border/50" />
            )}

            {/* Categories: FOMO Vision (Automated) with optional user category included */}
            {(bulletin.user_category || bulletin.auto_category || (Array.isArray(bulletin.content_labels) && bulletin.content_labels.length > 0)) && (
              <div className="space-y-3">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Categories</div>
                {Array.isArray(bulletin.content_labels) && bulletin.content_labels.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Eye size={12} className="opacity-70" />
                      <span>FOMO Vision (Automated)</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(bulletin.user_category || bulletin.auto_category) && (
                        <span className="px-2 py-1 rounded-full bg-muted/60 text-xs text-foreground/90">
                          {bulletin.user_category || bulletin.auto_category}
                        </span>
                      )}
                      {bulletin.content_labels.map((label: string) => (
                        <span key={label} className="px-2 py-1 rounded-full bg-muted/40 text-xs text-foreground/80">
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-border/50" />

            {/* Community section */}
            {(bulletin?.primary_community?.display_name || bulletin?.profiles?.community_tag) && (
              <div className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Community</div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted/50 text-sm font-medium w-fit">
                  {bulletin?.primary_community?.display_name || bulletin?.profiles?.community_tag}
                </div>
              </div>
            )}

            {/* Comments Section */}
            <div className="space-y-3">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Comments</div>
              {bulletin.loop_id ? (
                <div className="max-h-[400px] overflow-y-auto pr-2">
                  <LoopComments loopId={bulletin.loop_id} />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Comments are not available for this post. 
                  {bulletin.created_at && new Date(bulletin.created_at) < new Date('2025-01-01') ? 
                    ' (Legacy post created before comments integration)' : 
                    ' This may be a legacy post or there was an issue linking to the loops system.'}
                </p>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LightboxInfoPanel;
